import React from 'react'
import { Link } from 'react-router-dom'

import { Box, Checkbox, Tag, Text } from '@factorialco/deprecated-design-system'
import { WorkSpaceSlotFragment } from 'generated/resources/graphql'
import moment from 'moment'
import * as momentTimezone from 'moment-timezone'

import { observer } from 'mobx-react'

import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type { WorkSpaceSlotNode } from 'modules/space_control/hooks/useWorkSpaceSlots'

interface Props {
  slots: WorkSpaceSlotNode[]
  onChange: (value: number) => void
  selectedValues: number[]
  selectedDate: string
  isHalfDay?: boolean
  timezone?: string | null
  edit?: boolean
}

const BookingSlots = ({
  slots,
  onChange,
  selectedValues,
  selectedDate,
  isHalfDay = false,
  timezone = 'Europe/Madrid',
  edit = false
}: Props) => {
  const getLabel = (index: number, slot: WorkSpaceSlotNode) => {
    if (isHalfDay) {
      return index === 0
        ? i18n.t('space_control.overview.booking_sidepanel.time_slots.first_half')
        : i18n.t('space_control.overview.booking_sidepanel.time_slots.second_half')
    } else {
      return `${slot.startsAt} - ${slot.endsAt}`
    }
  }

  const isBooked = (slot: WorkSpaceSlotFragment) => slot.currentCapacity === 0

  const isExpired = (slot: WorkSpaceSlotFragment) => {
    if (!timezone) return false

    const nowInSpaceLocation = momentTimezone.tz(timezone)

    const [hour, minute] = slot.endsAt.split(':').map(Number)
    if (hour === undefined) return false
    const slotEndTime = momentTimezone.tz(selectedDate, timezone).set({ hour, minute })

    return nowInSpaceLocation.isAfter(slotEndTime)
  }

  const formattedDate = i18nDateToString(moment(selectedDate), 'ui')

  return (
    <Box gap="s4">
      {!edit && (
        <Text.Normal size="200" weight="medium" color="grey1000">
          {i18n.t('space_control.overview.booking_sidepanel.time_slots.label_with_date', {
            date: formattedDate
          })}
        </Text.Normal>
      )}
      {edit && (
        <Text.Normal size="200" weight="medium" color="grey1000">
          {i18n.t('space_control.overview.booking_sidepanel.time_slots.label')}
        </Text.Normal>
      )}
      <Box
        borderRadius={{ all: 'abs008' }}
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid'
          }
        }}
        gapDivider
        role="list"
        aria-label="slots"
      >
        {slots
          .filter((slot) => !isExpired(slot))
          .map((slot, i) => (
            <Box
              key={i}
              flexDirection="row"
              gap="s8"
              alignItems="center"
              justifyContent="spaceBetween"
              padding="s16"
              background={isBooked(slot) ? 'grey100' : undefined}
              role="listitem"
              aria-label={getLabel(i, slot)}
            >
              <Checkbox
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onChange(Number(event.target.value))
                }
                value={slot.slotNumber}
                checked={selectedValues.includes(slot.slotNumber)}
                disabled={isBooked(slot)}
                label={getLabel(i, slot)}
              />
              {isBooked(slot) && (
                <Box flexDirection="row" gap="s4" alignItems="center" flexWrap="wrap">
                  <Tag type="grey">
                    {i18n.t('space_control.overview.booking_sidepanel.time_slots.is_booked')}
                  </Tag>
                  {slot.bookerEmployeeIds && slot.bookerEmployeeIds.length > 0 && (
                    <Text.Normal size="100" color="grey700">
                      {i18n.t('space_control.overview.booking_sidepanel.time_slots.booked_by')}{' '}
                      {slot.bookerEmployeeIds
                        .map((employeeId, idx) => {
                          const name = slot.bookerNames?.[idx]
                          return name ? { employeeId, name } : null
                        })
                        .filter(
                          (booker): booker is { employeeId: number; name: string } =>
                            booker !== null
                        )
                        .map((booker, index, arr) => (
                          <React.Fragment key={`${booker.employeeId}-${index}`}>
                            <Link
                              to={`/employees/${booker.employeeId}`}
                              style={{ color: 'inherit', textDecoration: 'underline' }}
                            >
                              {booker.name}
                            </Link>
                            {index < arr.length - 1 && ', '}
                          </React.Fragment>
                        ))}
                    </Text.Normal>
                  )}
                </Box>
              )}
            </Box>
          ))}
      </Box>
    </Box>
  )
}

export default observer(BookingSlots)
